import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Box, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';

import { Dialog } from '@components/dialog';
import { QuoteStatus } from '@models/quotes';
import * as styles from '@components/modals/Modal.styles';
import { Form, FormSection } from '@components/forms';
import { ReactivateDeclinedRejectedModalFormValues } from './ReactivateDeclinedRejectedModalFormValues.type';
import { getFormFields } from './getFormFields';
import { Grid } from '@mui/system';
import { ToastType } from '@components/toast';
import { useReactivateDeclinedRejectedQuote } from '@services/quotes/useReactivateDeclinedRejectedQuote';
import { ReactivateQuotePostRequestBody, ReactivateQuoteRequestParams } from '@lio/api-types';

type ReactivateDeclinedRejectedModalProps = {
  isOpen: boolean;
  quoteId: string;
  policyNumber: string;
  currentStatusId: string;
  onCloseModal: () => void;
};

const getDefaultValues = () => ({
  reason: undefined,
  effectiveOn: new Date(),
  bypassBOR: false,
});

export const ReactivateDeclinedRejectedModal: React.FC<ReactivateDeclinedRejectedModalProps> = ({
  isOpen,
  quoteId,
  policyNumber,
  currentStatusId,
  onCloseModal,
}) => {
  const { reactivateQuote, isPending: isLoading } = useReactivateDeclinedRejectedQuote();

  const methods = useForm<ReactivateDeclinedRejectedModalFormValues>({
    defaultValues: getDefaultValues(),
    mode: 'onChange',
  });

  const { enqueueSnackbar } = useSnackbar();

  const handleExit = () => {
    clearForm();
    onCloseModal();
  };

  const clearForm = () => {
    methods.reset(getDefaultValues());
  };

  useEffect(() => {
    if (isOpen) {
      methods.reset(getDefaultValues());
    }
  }, [isOpen, methods]);

  const isValid = methods.formState.isValid;

  const fields = useMemo(() => {
    return getFormFields({ currentStatusId });
  }, [currentStatusId]);

  const handleReactivatedDeclinedRejectedQuote = async () => {
    if (!quoteId) {
      return;
    }

    const formData = methods.getValues();
    const reactivationQuoteBody: ReactivateQuotePostRequestBody & ReactivateQuoteRequestParams = {
      quoteId,
      reason: formData.bypassBOR ? 'Reactivate and bypass BOR' : 'Reactivate',
      description: formData.reason,
      policyEffectiveDate: formData.effectiveOn ?? undefined,
      allowBORBypass: formData.bypassBOR ?? undefined,
    };

    reactivateQuote(reactivationQuoteBody, {
      onError: () => {
        enqueueSnackbar(`Reactivation of quote #${policyNumber} has failed.`, {
          variant: ToastType.Error,
          preventDuplicate: true,
        });
      },
      onSuccess: () => {
        clearForm();
        onCloseModal();
        enqueueSnackbar(`Quote #${policyNumber} was successfully reactivated.`, {
          preventDuplicate: true,
          variant: ToastType.Success,
        });
      },
    });
  };

  return (
    <Dialog
      handleCloseDialog={handleExit}
      isPrimaryBtnDisabled={!isValid}
      isPrimaryBtnLoading={isLoading}
      open={isOpen}
      primaryBtnAction={handleReactivatedDeclinedRejectedQuote}
      primaryBtnText='Reactivate Quote'
      secondaryBtnAction={handleExit}
      secondaryBtnText='Exit'
      title={`Reactivation of ${QuoteStatus.Declined.id === currentStatusId ? 'Declined' : 'Rejected'} Quote #${policyNumber}`}
    >
      <Grid size={{ xs: 12 }} sx={styles.modalScrollContainer}>
        <Typography variant='bodySmall'>
          {QuoteStatus.Rejected.id === currentStatusId
            ? 'If this customer has a BOR conflict, it will be automatically rejected again unless “Bypass BOR” is selected. '
            : ''}
          Please do not reactivate quotes that are over 6 months old.
        </Typography>
      </Grid>
      <Form methods={methods} formId='reactivation-modal-form'>
        <Box sx={{ ...styles.modalContent, padding: 0 }}>
          <Box sx={styles.modalScrollContainer}>
            <FormSection fields={fields} styleConfig={{ spacing: 3 }} />
          </Box>
        </Box>
      </Form>
    </Dialog>
  );
};
